import { useLocation } from 'react-router-dom';
import { type ReactNode, useEffect } from 'react';
import { ErrorBoundary } from '@/components/ui/ErrorBoundary';
import { getLenis } from '@/lib/lenis';

/**
 * Wraps routed content in an ErrorBoundary whose React identity is reset on
 * every navigation. If page A throws, navigating to page B remounts a fresh
 * boundary instead of carrying the error state forward.
 *
 * Place this around <Routes> (or per-route) so a thrown error in one page
 * never bricks navigation to another.
 */
export function RouteBoundary({ children }: { children: ReactNode }) {
  const location = useLocation();

  // Lenis owns scroll position (smooth-scroll singleton), so a native
  // window.scrollTo(0,0) is silently ignored while it's active — that's why
  // clicking a suggested product or the wishlist icon didn't jump to top.
  // Reset through Lenis when present; fall back to native scroll under
  // reduced-motion / SSR (getLenis() returns null in those cases).
  // pathname-only dep is intentional: in-page filter changes on Category
  // mutate `?search` and must NOT yank the user back to the top.
  useEffect(() => {
    const lenis = getLenis();
    if (lenis) lenis.scrollTo(0, { immediate: true });
    else window.scrollTo(0, 0);
  }, [location.pathname]);

  return <ErrorBoundary key={location.pathname}>{children}</ErrorBoundary>;
}
